import { useState } from "react";
import { Columns2, SplitSquareHorizontal, Flame } from "lucide-react";
import { orthoUrl, heatUrl, compareSrc, compareLabel } from "@/lib/api";

const MODES = [
  { id: "split", label: "Naast elkaar", icon: Columns2 },
  { id: "swipe", label: "Schuif", icon: SplitSquareHorizontal },
  { id: "heat", label: "Verschilkaart", icon: Flame },
];

const Boxes = ({ regions, size, highlight }) =>
  (regions || []).map((r) => {
    const [x0, y0, x1, y1] = r.bbox_px;
    const hot = highlight?.includes(r.nr);
    return (
      <div key={r.nr} className={`absolute border-2 rounded-sm ${hot ? "border-red-500 pulse-box" : "border-amber-300/70 border-dashed"}`}
        style={{ left: `${(x0 / size) * 100}%`, top: `${(y0 / size) * 100}%`, width: `${((x1 - x0) / size) * 100}%`, height: `${((y1 - y0) / size) * 100}%` }}>
        <span className={`absolute -top-5 left-0 font-mono text-[10px] px-1 rounded-sm ${hot ? "bg-red-500" : "bg-amber-500 text-black"}`}>{r.nr}</span>
      </div>
    );
  });

const Frame = ({ src, label, children, testId }) => (
  <div className="relative aspect-square bg-slate-900 rounded-md overflow-hidden border border-slate-800" data-testid={testId}>
    <img src={src} alt={label} className="absolute inset-0 w-full h-full object-cover" />
    {children}
    <span className="absolute bottom-2 left-2 font-mono text-[11px] bg-black/70 px-2 py-0.5 rounded">{label}</span>
  </div>
);

export const CompareViewer = ({ addressId, analysis, highlight }) => {
  const [mode, setMode] = useState("split");
  const [pos, setPos] = useState(50);
  const [showBoxes, setShowBoxes] = useState(true);
  const { baseline_year: a, compare_year: b, regions, size, id } = analysis;
  const bSrc = compareSrc(addressId, analysis);
  const bLabel = compareLabel(analysis);
  const boxes = showBoxes && <Boxes regions={regions} size={size} highlight={highlight} />;

  return (
    <div data-testid="compare-viewer">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex gap-1 bg-slate-900 border border-slate-800 rounded-md p-1">
          {MODES.map(({ id: m, label, icon: Icon }) => (
            <button key={m} data-testid={`compare-mode-${m}`} onClick={() => setMode(m)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors ${mode === m ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}>
              <Icon className="h-3.5 w-3.5" />{label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
          <input type="checkbox" data-testid="toggle-region-boxes" checked={showBoxes} onChange={(e) => setShowBoxes(e.target.checked)} className="accent-blue-600" />
          Verschilregio's tonen
        </label>
      </div>
      {mode === "split" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Frame src={orthoUrl(addressId, a)} label={`BASIS ${a}`} testId="frame-baseline" />
          <Frame src={bSrc} label={bLabel.toUpperCase()} testId="frame-compare">{boxes}</Frame>
        </div>
      )}
      {mode === "swipe" && (
        <div className="max-w-[640px] mx-auto">
          <div className="relative aspect-square rounded-md overflow-hidden border border-slate-800 select-none" data-testid="swipe-view">
            <img src={bSrc} alt="nieuw" className="absolute inset-0 w-full h-full object-cover" />
            <img src={orthoUrl(addressId, a)} alt="basis" className="absolute inset-0 w-full h-full object-cover" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />
            {boxes}
            <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_12px_rgba(0,0,0,.8)]" style={{ left: `${pos}%` }} />
            <span className="absolute bottom-2 left-2 font-mono text-[11px] bg-black/70 px-2 py-0.5 rounded">{a}</span>
            <span className="absolute bottom-2 right-2 font-mono text-[11px] bg-black/70 px-2 py-0.5 rounded">{analysis.upload_id ? bLabel : b}</span>
          </div>
          <input type="range" min={0} max={100} value={pos} onChange={(e) => setPos(+e.target.value)} data-testid="compare-slider-handle" className="w-full mt-3 accent-blue-600" />
        </div>
      )}
      {mode === "heat" && (
        <div className="max-w-[640px] mx-auto">
          <Frame src={bSrc} label={`VERSCHIL ${a} → ${analysis.upload_id ? bLabel : b}`} testId="heat-view">
            <img src={heatUrl(id)} alt="heatmap" className="absolute inset-0 w-full h-full object-cover mix-blend-screen" />
            {boxes}
          </Frame>
        </div>
      )}
    </div>
  );
};
